import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { InternalLinks } from "@/components/InternalLinks";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { CreditCard, RefreshCw, ShieldCheck, Home, Sparkles, MapPin, Zap, Phone, Calendar } from "lucide-react";
import { useLocation } from "@/lib/locations";

const BOOKING_URL = "/book";

function buildFaqCategories(location: string) {
  const isSacramento = location === "Sacramento" || location === "Sacramento Area";

  return [
    {
      title: "Pricing Questions",
      icon: <CreditCard className="h-6 w-6 text-primary" />,
      questions: [
        {
          question: `How much does house cleaning cost in ${location}?`,
          answer: `Standard house cleaning in ${location} starts around $149. Pricing depends on your home's square footage, condition, and the type of service you choose. Our instant online quote tool gives you an exact flat-rate price in seconds — no walkthroughs, no guessing.`
        },
        {
          question: `What affects the price of a cleaning service in ${location}?`,
          answer: `Several factors influence your quote in ${location}: total square footage, level of buildup, whether you have pets, any add-on services (oven, fridge, laundry), and how frequently you schedule service.`
        },
        {
          question: `Are there discounts for recurring cleaning in ${location}?`,
          answer: `Yes! Recurring clients in ${location} receive meaningful discounts. Weekly service saves you 35%, biweekly saves 30%, and monthly plans save 20% compared to one-time pricing. It's our way of rewarding loyal local customers.`
        },
        {
          question: "Do you offer free quotes?",
          answer: "Absolutely. You can get an instant, accurate quote online in seconds using our pricing calculator — no phone call or in-home estimate required. You can also call us directly at (916) 299-5588."
        },
        {
          question: "Do you charge by the hour?",
          answer: "No. We prefer flat-rate pricing based on your home's size and the service type you select. This means no surprise charges if a clean takes longer — you always know exactly what you're paying upfront."
        },
        {
          question: "Are there extra fees for heavy buildup?",
          answer: "Sometimes, if the home requires significantly more labor due to heavy buildup. We'll always communicate this transparently before your appointment so there are no surprises."
        },
        {
          question: "How much is move-out cleaning?",
          answer: "Move-out cleaning varies by size and condition and is usually priced higher than a standard clean because of the detailed, comprehensive work involved. Use our online calculator or call us for a quick quote."
        },
        {
          question: "Do you require deposits?",
          answer: "Some appointments — especially large homes, move-out cleans, or specialty jobs — may require a deposit to secure the booking. Details are always communicated clearly at the time of scheduling."
        },
        {
          question: "Are supplies included in the price?",
          answer: "Yes. Standard supplies and equipment are included in your quoted price. We only ask that you provide a vacuum to prevent cross-contamination of allergens between homes."
        },
        {
          question: "Can I customize my service to fit my budget?",
          answer: "Yes. We can prioritize certain areas and build a custom plan that fits both your home's needs and your budget. Just let us know what matters most when booking."
        }
      ]
    },
    {
      title: "Recurring Cleaning Questions",
      icon: <RefreshCw className="h-6 w-6 text-primary" />,
      questions: [
        {
          question: "How often should I schedule house cleaning?",
          answer: `Most of our ${location} clients choose biweekly service as the best balance of cleanliness and value. Weekly is highly popular for busy families with children and pets. Monthly works well for those who want a refresh without a large commitment.`
        },
        {
          question: "What recurring plans do you offer?",
          answer: "We offer three recurring service plans: Weekly (35% off), Biweekly (30% off), and Monthly (20% off). All plans include priority scheduling and the same consistent team whenever possible."
        },
        {
          question: "Is weekly cleaning worth it?",
          answer: "Definitely — especially for homes with children, pets, or high foot traffic. Weekly service keeps buildup minimal, reduces allergens, and means each visit is faster and more thorough since the home never gets too dirty between visits."
        },
        {
          question: "Is biweekly cleaning the most popular option?",
          answer: `Yes! Biweekly is our most popular plan. It strikes the ideal balance between maintaining a consistently clean home and keeping costs manageable. Most ${location} families find every two weeks hits the sweet spot.`
        },
        {
          question: "Can I skip a recurring visit?",
          answer: "Usually yes, with advance notice. We understand life happens. Just let us know as early as possible — ideally 24 hours before your scheduled appointment — and we'll adjust your plan accordingly."
        },
        {
          question: "Can I cancel recurring service anytime?",
          answer: "Yes. There are no long-term contracts. You can cancel or pause your recurring service at any time, unless a special arrangement or promotional rate was agreed upon at signup."
        },
        {
          question: "Will I get the same cleaner each time?",
          answer: "We try to provide consistency whenever possible. We make every effort to send the same trusted professional to your home for every recurring visit. If your regular cleaner is unavailable, we'll notify you in advance."
        },
        {
          question: "Do recurring customers get priority scheduling?",
          answer: "Yes! Recurring clients typically receive priority booking, which means you'll always have a consistent spot on our schedule. This is especially valuable during busy seasons like spring cleaning and the holidays."
        },
        {
          question: "Can recurring plans be changed later?",
          answer: "Yes. You can often switch frequencies (e.g., from monthly to biweekly) as needed. Just contact us and we'll update your plan and adjust pricing accordingly."
        },
        {
          question: "Why is recurring cleaning better than one-time service?",
          answer: "It keeps buildup low and maintains a consistently clean home. Recurring cleaning is better for your home's long-term condition, better for your health (fewer allergens), and more cost-effective thanks to our recurring discounts."
        }
      ]
    },
    {
      title: "Service Questions",
      icon: <Home className="h-6 w-6 text-primary" />,
      questions: [
        {
          question: "What is included in a standard cleaning?",
          answer: "Our standard clean covers all bathrooms (toilets, sinks, showers, mirrors), kitchen surfaces (countertops, stovetop, sink, cabinet exteriors), all floors (vacuumed and mopped), dusting of surfaces and furniture, emptying trash, and general tidying throughout the home."
        },
        {
          question: "What is included in a deep cleaning?",
          answer: "A deep clean goes well beyond the standard service. It includes detailed attention to baseboards, light switches, door handles, interior window sills, inside the microwave, removal of built-up grime in bathrooms and kitchens, and thorough dusting of hard-to-reach areas. We recommend a deep clean for first-time clients."
        },
        {
          question: "What is move-out cleaning?",
          answer: "Move-out cleaning is a comprehensive, top-to-bottom service designed to prepare a home for handoff, sale, or final inspection. Every room is cleaned in detail, including inside appliances, cabinets, closets, and all surfaces. It's our most thorough service and is landlord- and real estate-approved."
        },
        {
          question: "Do you clean inside ovens?",
          answer: "Yes! Inside oven cleaning is available as an add-on service. It's especially popular with our deep clean and move-out packages. Just select it when booking online."
        },
        {
          question: "Do you clean inside refrigerators?",
          answer: "Yes. Interior refrigerator cleaning is available as an add-on. We remove all shelves and drawers, clean all surfaces, and wipe down the door seals. Please empty your fridge before the appointment for best results."
        },
        {
          question: "Do you wash dishes?",
          answer: "Light dish loading (loading the dishwasher) may be available by request. Full dish washing is not a standard service but can be discussed as a custom add-on during booking."
        },
        {
          question: "Do you do laundry?",
          answer: "Laundry folding may be available as an add-on for select plans. Washing and drying can sometimes be arranged as a custom request. Contact us to discuss your specific needs."
        },
        {
          question: "Do you make beds?",
          answer: "Yes! Bed making is included when requested. We'll make your beds with fresh linens if provided, or neatly straighten the existing bedding."
        },
        {
          question: "Do you clean windows?",
          answer: "Interior window cleaning (wiping down glass, sills, and frames) is available as an add-on service. Exterior window cleaning is not offered at this time."
        },
        {
          question: "Do you clean baseboards?",
          answer: `Yes! Baseboard cleaning is included in our deep clean service and available as an add-on for standard cleans. It's one of the most appreciated details our ${location} clients notice after a thorough visit.`
        }
      ]
    },
    {
      title: "Trust & Logistics",
      icon: <ShieldCheck className="h-6 w-6 text-primary" />,
      questions: [
        {
          question: `Are you licensed and insured in ${location}?`,
          answer: `Yes. Uplevel Cleaning is fully licensed, insured, and bonded in ${location}. This protects both your home and our team in the unlikely event of an accident or damage during service.`
        },
        {
          question: `Do I need to be home during service in ${location}?`,
          answer: `No. The majority of our ${location} clients are not home during their cleaning. You simply provide entry instructions at the time of booking and we handle the rest securely and professionally.`
        },
        {
          question: "How do cleaners enter my home?",
          answer: "Common entry methods include a lockbox code, hidden key, garage door code, or being home to let us in. All access information is stored securely and only used by your assigned cleaning professional."
        },
        {
          question: "How long does a cleaning take?",
          answer: "Cleaning time varies based on home size and condition. Most standard cleans take 2–5 hours depending on size. Deep cleans and move-out cleans typically take 3–5 hours. Your exact time window is shared at booking."
        },
        {
          question: "What products do you use?",
          answer: "We use professional cleaning products that are effective and safe for your family. If you have specific product preferences or sensitivities, let us know and we'll accommodate them."
        },
        {
          question: "Are your products pet safe?",
          answer: "Many standard products are pet-conscious when used properly. Our cleaners are also trained to work safely around animals. If your pet is anxious around strangers, we recommend making temporary arrangements."
        },
        {
          question: "What if I'm not satisfied?",
          answer: "Contact us promptly and we'll work to make it right. Our 200% Ironclad Guarantee means we'll reclean any missed areas for free within 48 hours. If you're still not satisfied, we'll issue a full refund. No questions asked."
        },
        {
          question: "Do I need to tip cleaners?",
          answer: "Tips are appreciated but never required or expected. If you'd like to show appreciation, any amount is gratefully received. You can tip in cash or through your online account."
        },
        {
          question: "What if I need to reschedule?",
          answer: "Contact us as early as possible — ideally at least 24 hours before your appointment. You can reschedule easily through your online portal or by calling (916) 299-5588. We'll find the next available time that works for you."
        },
        {
          question: "How far in advance should I book?",
          answer: `1–7 days in advance is ideal to secure your preferred time slot in ${location}. Same-week and even same-day openings are sometimes available depending on our schedule. The sooner you book, the more options you have.`
        }
      ]
    },
    {
      title: `${location} Local Questions`,
      icon: <MapPin className="h-6 w-6 text-primary" />,
      questions: [
        {
          question: `What areas do you serve near ${location}?`,
          answer: isSacramento
            ? "We proudly serve Sacramento, Roseville, Folsom, Elk Grove, Rancho Cordova, Citrus Heights, Granite Bay, El Dorado Hills, West Sacramento, Carmichael, Davis, and Rocklin — plus many other nearby communities throughout the greater Sacramento region."
            : `We serve ${location} and all surrounding communities in the area. Use the zip code checker on our homepage to confirm your address is within our coverage zone.`
        },
        {
          question: `Do you clean apartments in ${location}?`,
          answer: `Yes! We clean apartments, condos, townhomes, and houses throughout ${location}. Whether it's a compact studio or a large multi-bedroom unit, we have the right plan for your space.`
        },
        {
          question: `Do you clean condos and townhomes in ${location}?`,
          answer: `Yes. We clean all residential property types including condos, townhomes, single-family homes, and apartments throughout ${location} and surrounding areas.`
        },
        ...(isSacramento ? [
          {
            question: "Do you clean homes in Roseville?",
            answer: "Yes. Roseville is one of our most active service areas. We serve homes throughout Roseville including neighborhoods near West Roseville, Galleria, and surrounding communities."
          },
          {
            question: "Do you clean homes in Folsom?",
            answer: "Yes! Folsom is a key part of our service area. We serve homes near Folsom Lake, Empire Ranch, and all surrounding Folsom neighborhoods."
          },
          {
            question: "Do you clean homes in Elk Grove?",
            answer: "Absolutely. Elk Grove is one of our busiest markets. We serve all Elk Grove neighborhoods including Laguna, Lakeside, Sterling Meadows, and more."
          },
        ] : []),
        {
          question: `Do you clean Airbnb rentals in ${location}?`,
          answer: `Yes! We offer specialized short-term rental and Airbnb turnover cleaning services in ${location} and surrounding areas. Fast turnovers, consistent quality, and flexible scheduling are our specialties for rental hosts.`
        },
        {
          question: `Do you offer office cleaning in ${location}?`,
          answer: `Yes. Our commercial cleaning services cover offices, retail spaces, and small businesses throughout ${location}. We offer after-hours scheduling and customized cleaning scopes to fit your workplace needs.`
        },
        {
          question: `Can I book same-week cleaning in ${location}?`,
          answer: `Often yes! We frequently have same-week openings in ${location} and surrounding areas. Use our online booking tool to see available slots — or call (916) 299-5588 for our fastest availability.`
        },
        {
          question: `Why choose Uplevel Cleaning over other ${location} cleaners?`,
          answer: `Uplevel Cleaning combines the reliability of a professional company with the care of a local service. We offer upfront flat-rate pricing, 60-second online booking, rigorous cleaner vetting, eco-friendly products, and our 200% Ironclad Satisfaction Guarantee. We don't just clean homes — we uplevel them.`
        }
      ]
    }
  ];
}

export default function FAQPage() {
  const loc = useLocation();
  const locationName = loc.name;
  const faqCategories = buildFaqCategories(locationName);
  const totalQuestions = faqCategories.reduce((sum, cat) => sum + cat.questions.length, 0);

  return (
    <div className="flex min-h-screen flex-col">
      <SEO 
        title={`FAQ — Uplevel Cleaning ${locationName} | ${totalQuestions} Questions Answered`}
        description={`Get answers to all your questions about Uplevel Cleaning's ${locationName} house cleaning services. Pricing, recurring plans, what's included, pet safety, and more.`}
        keywords={`cleaning faq ${locationName.toLowerCase()}, house cleaning cost ${locationName.toLowerCase()}, maid service questions, recurring cleaning ${locationName.toLowerCase()}, home cleaning faq, uplevel cleaning questions`}
        url="https://uplevelcleaning.com/faq"
        locationName={locationName}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "FAQ", url: "/faq" }
        ]}
      />
      <Navbar />
      <main className="flex-1 bg-muted/30 py-16 md:py-24">
        <div className="container max-w-5xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Sparkles className="h-4 w-4" /> {totalQuestions} Questions Answered
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Frequently Asked Questions</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Everything you need to know about Uplevel Cleaning's services in {locationName} and beyond. Can't find your answer? Call us anytime.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Button asChild size="lg" className="font-bold bg-primary text-primary-foreground hover:bg-primary-hover h-12 px-8">
                <a href={BOOKING_URL} aria-label="Get instant price from FAQ page">
                  <Zap className="mr-2 h-5 w-5 fill-primary-foreground/20" />
                  Get Instant Price
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="font-bold border-primary text-primary hover:bg-primary/10 hover:text-primary h-12 px-8">
                <a href="tel:9162995588">
                  <Phone className="mr-2 h-5 w-5" />
                  Call (916) 299-5588
                </a>
              </Button>
            </div>
          </div>

          {/* Quick Nav */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {faqCategories.map((cat, idx) => (
              <a
                key={idx}
                href={`#category-${idx}`}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-background border border-border hover:border-primary hover:text-primary text-sm font-medium transition-colors"
              >
                {cat.icon}
                {cat.title}
              </a>
            ))}
          </div>

          <div className="grid gap-8">
            {faqCategories.map((category, idx) => (
              <div key={idx} id={`category-${idx}`} className="bg-background rounded-2xl p-8 shadow-sm border scroll-mt-28">
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-3 rounded-xl bg-primary/10">
                    {category.icon}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{category.title}</h2>
                    <p className="text-sm text-muted-foreground">{category.questions.length} questions answered</p>
                  </div>
                </div>
                
                <Accordion type="single" collapsible className="w-full space-y-2">
                  {category.questions.map((faq, i) => (
                    <AccordionItem key={i} value={`item-${idx}-${i}`} className="border-none">
                      <AccordionTrigger className="text-left font-semibold py-4 hover:no-underline hover:text-primary transition-colors text-base md:text-lg">
                        <span className="flex gap-3 items-start">
                          <span className="text-primary font-bold shrink-0 mt-0.5 text-sm">
                            {faqCategories.slice(0, idx).reduce((sum, c) => sum + c.questions.length, 0) + i + 1}.
                          </span>
                          {faq.question}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed text-base pt-2 pb-4 pl-7">
                        {faq.answer}
                      </AccordionContent>
                      <div className="h-px bg-border w-full" />
                    </AccordionItem>
                  ))}
                </Accordion>

                <div className="mt-6 pt-4 border-t">
                  <Button asChild size="sm" className="font-semibold bg-primary text-primary-foreground hover:bg-primary-hover">
                    <a href={BOOKING_URL}>
                      <Calendar className="mr-2 h-4 w-4" />
                      Book Now — Instant Price
                    </a>
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-20 bg-primary rounded-3xl p-12 text-center text-primary-foreground relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Still have questions?</h2>
              <p className="text-xl mb-10 opacity-90 max-w-2xl mx-auto">
                Our friendly {locationName} support team is available 7 days a week to help you find the perfect cleaning plan.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" variant="secondary" className="font-bold text-lg h-14 px-10">
                  <a href={BOOKING_URL}>Book My Clean Now</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-primary font-bold text-lg h-14 px-10">
                  <a href="tel:9162995588">Call (916) 299-5588</a>
                </Button>
              </div>
            </div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full -ml-32 -mb-32 blur-3xl" />
          </div>
        </div>
      </main>
        <div className="container mt-16 max-w-4xl text-center">
          <h3 className="text-2xl font-bold mb-4">Still have questions?</h3>
          <p className="text-muted-foreground mb-8">
            Check out our <a href="/blog" className="text-primary hover:underline">Cleaning Blog</a> for tips and tricks, or visit our <a href="/pricing" className="text-primary hover:underline">Pricing Page</a> to see our transparent flat-rate options.
          </p>
        </div>

        <InternalLinks />
        <Footer />
    </div>
  );
}
